import React, { useState, useEffect, useMemo } from 'react';
import BalanceRadarChart from '../../components/balanceRadarChart';
import {
    useTheme,
    Box,
    Typography,
    Grid,
    useMediaQuery,
    } from '@mui/material';
import buildingsBackground from '../../assets/images/HIR-FONDO.png';
import { publicAxios } from '../../apiClients';
import BackButton from '../../components/BackButton';
import SelectOptionMM from '../../components/SelectOptionMM';
import Loading from '../../components/Loading';
import HMLogo from '../../assets/banners/HM-blanco.png';

// Función auxiliar para convertir hex a rgba#
const hexToRgba = (hex, alpha) => {
const hexValue = hex.replace('#', '');
const r = parseInt(hexValue.substring(0, 2), 16);
const g = parseInt(hexValue.substring(2, 4), 16);
const b = parseInt(hexValue.substring(4, 6), 16);

return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const MarketComparativeHealth = () => {
    const theme = useTheme();

    // Uso de useMediaQuery para detectar pantallas pequeñas
    const isMobile = useMediaQuery('(max-width:600px)');

    // Estado para la segunda firma seleccionada (sin incluir Firma 1)
    const [selectedFirma, setSelectedFirma] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [firm, setFirm] = useState(null);
    const [otherFirms, setOtherFirms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        const firmId = localStorage.getItem('organizationId');
        let isMounted = true;

        const fetchData = async () => {
            try {
                const [firmResponse, otherResponse] = await Promise.all([
                    publicAxios.get(`/institution/market-monitor-health/${firmId}`),
                    publicAxios.get('/institution/market-monitor-health/comparative'),
                ]);

                if (!isMounted) {
                    return;
                }

                setFirm(firmResponse.data);
                setOtherFirms(otherResponse.data);
            } catch (error) {
                console.error('Error fetching firm data:', error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        

    fetchData();

        return () => {
            isMounted = false;
        };
    }, []);

    const firmMetrics = [
        firm?.totalScore,
        firm?.brandPower,
        firm?.trajectory,
        firm?.arena,
        firm?.reputation,
    ];

    const getCountryName = (item) => (
        item?.Country?.name ?? item?.country?.name ?? ''
    );

    const getRegionName = (item) => (
        item?.Country?.region?.name ??
        item?.Country?.region ??
        item?.country?.region?.name ??
        item?.country?.region ??
        ''
    );

    const uniqueRegions = useMemo(() => {
        return [...new Set(
            otherFirms
                .map((firmItem) => getRegionName(firmItem))
                .filter((region) => Boolean(region))
        )]
            .sort((a, b) => a.localeCompare(b))
            .map((region) => ({ id: region, name: region }));
    }, [otherFirms, getRegionName]);

    const uniqueCountries = useMemo(() => {
        const source = selectedRegion
            ? otherFirms.filter((firmItem) => getRegionName(firmItem) === selectedRegion)
            : otherFirms;

        return [...new Set(
            source
                .map((firmItem) => getCountryName(firmItem))
                .filter((country) => Boolean(country))
        )]
            .sort((a, b) => a.localeCompare(b))
            .map((country) => ({ id: country, name: country }));
    }, [otherFirms, selectedRegion, getCountryName, getRegionName]);

    const filteredOtherFirms = useMemo(() => {
        return otherFirms.filter((firmItem) => {
            const countryName = getCountryName(firmItem);
            const regionName = getRegionName(firmItem);
            const matchesCountry = !selectedCountry || countryName === selectedCountry;
            const matchesRegion = !selectedRegion || regionName === selectedRegion;
            return matchesCountry && matchesRegion;
        });
    }, [otherFirms, selectedCountry, selectedRegion, getCountryName, getRegionName]);

    const firmas = useMemo(() => (
        filteredOtherFirms
            .slice()
            .sort((a, b) => a.institutionName.localeCompare(b.institutionName))
            .reduce((acc, f) => {
                acc[f.institutionName] = {
                    institutionName: f.institutionName,
                    totalScore: f.totalScore,
                    country: getCountryName(f),
                    metrics: [
                        f.totalScore,
                        f.brandPower,
                        f.trajectory,
                        f.arena,
                        f.reputation,
                    ],
                };
                return acc;
            }, {})
    ), [filteredOtherFirms, getCountryName]);

    useEffect(() => {
        if (selectedFirma && !firmas[selectedFirma]) {
            setSelectedFirma('');
        }
    }, [selectedFirma, firmas]);

    if (isLoading) {
        return (
            <Loading
                background="#6ec1e4"
                barTrackColor="rgba(255, 255, 255, 0.45)"
                barColor="#3592D2"
                logo={HMLogo}
                logoAlt="Health Market Monitor"
            />
        );
    }

    const handleCountryChange = (newCountry) => {
        setSelectedCountry(newCountry || '');
        setSelectedRegion('');
    };

    const handleRegionChange = (newRegion) => {
        setSelectedRegion(newRegion || '');
        setSelectedCountry('');
    };

    const primaryColor = '#054169';
    const secondaryColor = '#141a66';//theme.palette.primary.main; // Color para la segunda firma

    // Mapa de colores para las firmas
    const firmColors = {
        'Firma 1': primaryColor,
        'Firma 2': secondaryColor,
        // Agrega más firmas y sus colores según sea necesario
    };

    // Generar datasets para "Firma 1" y la firma adicional seleccionada
    const datasets = [
        {
        label: 'Firma 1',
        data: firmMetrics,
        fill: true,
        borderColor: firmColors['Firma 1'] || '#888888',
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
        backgroundColor: (ctx) => {
            const { chartArea, ctx: context } = ctx.chart;
            if (!chartArea) return;

            const gradient = context.createRadialGradient(
            chartArea.width / 2,
            chartArea.height / 2,
            0,
            chartArea.width / 2,
            chartArea.height / 2,
            chartArea.width / 2
            );

            const colorStart = hexToRgba(firmColors['Firma 1'], 0.2);
            const colorMid = hexToRgba(firmColors['Firma 1'], 0.5);
            const colorEnd = hexToRgba(firmColors['Firma 1'], 0.8);

            gradient.addColorStop(0, colorStart);
            gradient.addColorStop(0.5, colorMid);
            gradient.addColorStop(1, colorEnd);

            return gradient;
        },
        },
    ];

    if (selectedFirma && firmas[selectedFirma]) {
        datasets.push({
        label: selectedFirma,
        data: firmas[selectedFirma]?.metrics,
        fill: true,
        borderColor: firmColors[selectedFirma] || secondaryColor,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
        backgroundColor: (ctx) => {
            const { chartArea, ctx: context } = ctx.chart;
            if (!chartArea) return;

            const gradient = context.createRadialGradient(
            chartArea.width / 2,
            chartArea.height / 2,
            0,
            chartArea.width / 2,
            chartArea.height / 2,
            chartArea.width / 2
            );

            const colorStart = hexToRgba(
            firmColors[selectedFirma] || secondaryColor,
            0.2
            );
            const colorMid = hexToRgba(
            firmColors[selectedFirma] || secondaryColor,
            0.5
            );
            const colorEnd = hexToRgba(
            firmColors[selectedFirma] || secondaryColor,
            0.8
            );

            gradient.addColorStop(0, colorStart);
            gradient.addColorStop(0.5, colorMid);
            gradient.addColorStop(1, colorEnd);

            return gradient;
        },
        });
    }

    const data = {
        labels: [
        'Total',
        'Visibility',
        'Trajectory',
        'Arena',
        'Reputation',
        ],
        datasets, // Usar el array de datasets actualizado
    };

    const options = {
        maintainAspectRatio: false,
        scales: {
        r: {
            type: 'customRadial',
            min: 0,
            max: 100,
            ticks: {
            min: 0,
            beginAtZero: true,
            stepSize: 20,
            showLabelBackdrop: false,
            color: theme.palette.gray4,
            padding: 10,
            labelOffset: 5,
            font: {
                size: 0,
                family: theme.typography.fontFamily,
                lineHeight: 1.5,
            },
            },
        angleLines: {
            display: true,
            color: '#FFFFFF',
            },
            grid: {
            display: true,
            color: '#FFFFFF',
            },
            pointLabels: {
            color: '#000000',
            font: {
                size: isMobile ? 9 : 13,
                family: theme.typography.fontFamily,
            },
            callback: function (label) {
                if (label === 'Individual Performance') {
                return ['Individual', 'Performance'];
                }
                return label;
            },
            },
        },
        },
        plugins: {
        legend: {
            display: false, // No mostrar leyenda
        },
        tooltip: {
            backgroundColor: theme.palette.common.white,
            titleColor: theme.palette.gray1,
            bodyColor: theme.palette.gray2,
            borderWidth: 1,
            borderColor: theme.palette.gray4,
            titleFont: {
            size: 12,
            family: theme.typography.fontFamily,
            },
            bodyFont: {
            size: 12,
            family: theme.typography.fontFamily,
            },
        },
        },
    };

    return (
        <Box
        sx={{
            backgroundImage: `url(${buildingsBackground})`,
            backgroundSize: 'cover',
            minHeight: isMobile ? '100vh' : 'auto', // Cambiado de 'height' a 'minHeight'
            height: isMobile ? 'auto' : '120vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pb: 27,
            pt: 1,
        }}
        >
        <Grid container justifyContent="start" sx={{ mt: 0, ml: 1, mb: 0 }}>
            <BackButton path={-1} style={{ color: '#141a66' }} />
        </Grid>
        {/* Select sobre el Box horizontal */}
        <Box sx={{ width: isMobile ? '90%' : '60%', mb: 5 }}>
            <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={4}>
                    <SelectOptionMM
                        options={uniqueRegions.length > 0 ? uniqueRegions : [{ id: '', name: 'No options' }]}
                        onSelectionChange={handleRegionChange}
                        name="Filter by Region"
                        width="100%"
                        selectedOption={selectedRegion}
                        mmcolor="#333333"
                        focusColor="#3592D2"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <SelectOptionMM
                        options={uniqueCountries.length > 0 ? uniqueCountries : [{ id: '', name: 'No options' }]}
                        onSelectionChange={handleCountryChange}
                        name="Filter by Country"
                        width="100%"
                        selectedOption={selectedCountry}
                        mmcolor="#333333"
                        focusColor="#3592D2"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <SelectOptionMM
                        options={Object.keys(firmas).map((key) => ({ id: key, name: key }))}
                        onSelectionChange={setSelectedFirma}
                        name="Choose a firm"
                        width="100%"
                        selectedOption={selectedFirma}
                        mmcolor="#333333"
                        focusColor="#3592D2"
                    />
                </Grid>
            </Grid>
        </Box>

        {/* Caja azul transparente */}
        <Box
            sx={{
            backgroundColor: '#6ec1e4',
            width: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 14,
            ...(isMobile && {
                flexDirection: 'column',
                px: 2,
                py: 4,
            }),
            }}
        >
            {/* Puntaje izquierdo */}
            <Box
            sx={{
                textAlign: 'left',
                width: isMobile ? '100%' : '20%',
                marginLeft: '10px',
                marginTop: isMobile ? '28px' : 0,
                ...(isMobile && { order: 2 }),
            }}
            >
            <Box sx={{ display: 'flex-start', alignItems: 'center' }}>
                <Box
                sx={{
                    width: '22px',
                    height: '22px',
                    backgroundColor: primaryColor,
                    borderRadius: '50%',
                    marginRight: '10%',
                    marginBottom: '7%',
                }}
                />
                <Typography variant="h5" color='#141a66'>
                {firm?.institutionName}
                </Typography>
            </Box>
            <Typography fontSize={17} color='#141a66'>
                {firm?.Country.name}
            </Typography>
            <Typography variant="h3" color='#141a66'>
                {firm?.totalScore || 'N/A'}
            </Typography>
            <Typography color="#141a66">Total Score</Typography>
            </Box>

            {/* Gráfico en el centro */}
            <Box
            sx={{
                position: 'relative',
                borderRadius: '16px',
                paddingY: isMobile ? '0px' : '2%',
                height: isMobile ? '100%' : '95%',
                width: isMobile ? '100%' : '55%',
                ...(isMobile && {
                order: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px', // Establecer una altura mínima menor en pantallas pequeñas
                }),
                ...(!isMobile && {
                minHeight: '500px',
                }),
            }}
            >
                <BalanceRadarChart data={data} options={options} />
            </Box>

            {/* Puntaje derecho */}
            <Box
            sx={{
                textAlign: 'right',
                width: isMobile ? '100%' : '20%',
                marginRight: '10px',
                marginTop: isMobile ? '2%' : 0,
                marginBottom: isMobile ? '15%' : 0,
                ...(isMobile && { order: 4 }),
            }}
            >
            <Box
                sx={{
                alignItems: 'center',
                justifyContent: 'flex-end',
                }}
            >
                <Box
                sx={{
                    width: '22px',
                    height: '22px',
                    backgroundColor: "#ffffff",
                    borderRadius: '50%',
                    marginBottom: '7%',
                    marginLeft: isMobile ? '95%' : '91%',
                }}
                />
                <Typography variant="h5" color="#ffffff">
                {firmas[selectedFirma]?.institutionName}
                </Typography>
                <Typography fontSize={17} color="#ffffff">
                    {firmas[selectedFirma]?.country}
                </Typography>
                <Typography variant="h3" color="#ffffff">
                {selectedFirma
                    ? firmas[selectedFirma]?.totalScore
                    ? firmas[selectedFirma].totalScore
                    : 'N/A'
                    : ''}
                </Typography>
                <Typography color="#ffffff">Total Score</Typography>
            </Box>
            </Box>

        </Box>
        </Box>
    );
};

export default MarketComparativeHealth;
